import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Logo } from '@/components/ui/Logo';
import { useAuth } from '@/hooks/useAuth';
import { meetingsApi, type MeetingListItem } from '@/lib/meetings';

const FILTERS: Array<{ key: 'all' | 'draft' | 'live' | 'completed'; label: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'draft', label: 'Черновики' },
  { key: 'live', label: 'В эфире' },
  { key: 'completed', label: 'Завершённые' },
];

export function HomePage() {
  const { state } = useAuth();
  const name = state.status === 'authed' ? state.me.user.display_name : '';
  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    meetingsApi
      .list(filter === 'all' ? undefined : filter)
      .then((list) => {
        if (!cancelled) setMeetings(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <div className="min-h-dvh flex flex-col">
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo size={36} animated={false} />
          <div>
            <div className="text-[15px] font-semibold leading-tight">Eye Laba</div>
            <div className="text-[11px] text-ink-3 leading-tight">
              {name ? `Привет, ${name}` : ''}
            </div>
          </div>
        </div>
        <Link to="/settings" className="btn-ghost size-10 !p-0" aria-label="Настройки">
          <SettingsIcon />
        </Link>
      </header>

      <section className="px-5 pt-2">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="overflow-hidden relative">
            <div
              aria-hidden
              className="absolute -top-8 -right-8 size-44 rounded-full bg-accent/20 blur-2xl pointer-events-none"
            />
            <div className="relative">
              <div className="text-[13px] uppercase tracking-wider text-ink-3">
                Новая встреча
              </div>
              <div className="mt-1 text-[20px] font-semibold leading-snug">
                Опиши контекст —<br />
                и я буду слушать с тобой.
              </div>
              <Link to="/m/new">
                <Button full size="lg" className="mt-5">
                  <PlusIcon /> Создать встречу
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>
      </section>

      <section className="px-5 mt-6 flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] uppercase tracking-wider text-ink-3">
            История
          </div>
          <div className="flex gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={clsx(
                  'rounded-pill px-3 py-1 text-[12px] transition',
                  filter === f.key
                    ? 'bg-white/[0.08] text-ink-1'
                    : 'text-ink-3 hover:text-ink-2',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Card className="text-center py-10 text-ink-3">Загружаю…</Card>
        ) : meetings.length === 0 ? (
          <Card className="text-center py-10 text-ink-3">
            Здесь будут ваши встречи.
          </Card>
        ) : (
          <ul className="space-y-3">
            {meetings.map((m) => (
              <MeetingRow key={m.id} m={m} />
            ))}
          </ul>
        )}
      </section>

      <footer className="p-5 text-center text-[11px] text-ink-3">
        eye.laba.bot · v0.1
      </footer>
    </div>
  );
}

function MeetingRow({ m }: { m: MeetingListItem }) {
  const target = m.status === 'completed' ? `/m/${m.id}` : `/m/${m.id}/edit`;
  const dur = m.duration_seconds
    ? `${Math.round(m.duration_seconds / 60)} мин`
    : null;
  return (
    <li>
      <Link to={target}>
        <Card className="flex items-center justify-between gap-3 hover:bg-white/[0.06] transition active:scale-[0.997]">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <StatusDot s={m.status} />
              <h3 className="font-medium truncate">{m.title}</h3>
            </div>
            <div className="text-[11px] text-ink-3 mt-1 flex items-center gap-2">
              <span>{formatDate(m.started_at ?? m.created_at)}</span>
              {dur && <span>· {dur}</span>}
              <span>· {m.participants_count} уч.</span>
              {m.has_summary ? <span>· конспект</span> : null}
            </div>
          </div>
          <ChevronIcon />
        </Card>
      </Link>
    </li>
  );
}

function StatusDot({ s }: { s: MeetingListItem['status'] }) {
  const cls =
    s === 'live'
      ? 'bg-success animate-pulseSoft shadow-[0_0_8px_rgba(34,211,168,0.7)]'
      : s === 'draft'
        ? 'bg-warning'
        : s === 'completed'
          ? 'bg-accent'
          : 'bg-ink-3';
  return <span className={clsx('inline-block size-2 rounded-full', cls)} />;
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="text-ink-3 shrink-0">
      <path
        d="M8 4l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M19.43 12.98a7.957 7.957 0 0 0 .07-.98c0-.34-.03-.67-.07-.99l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.61-.22l-2.49 1a7.658 7.658 0 0 0-1.7-.99l-.38-2.65A.5.5 0 0 0 14 2h-4a.5.5 0 0 0-.5.42l-.38 2.65c-.61.25-1.18.58-1.7.99l-2.49-1a.5.5 0 0 0-.61.22l-2 3.46a.5.5 0 0 0 .12.64L4.55 11c-.04.32-.07.65-.07.98s.03.66.07.99l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46c.14.24.42.34.61.22l2.49-1c.52.41 1.09.74 1.7.99l.38 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.38-2.65c.61-.25 1.18-.58 1.7-.99l2.49 1c.19.12.47.02.61-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}
